function asyncGeneratorStep(n, t, e, r, o, a, c) { try { var i = n[a](c), u = i.value; } catch (n) { return void e(n); } i.done ? t(u) : Promise.resolve(u).then(r, o); }
function _asyncToGenerator(n) { return function () { var t = this, e = arguments; return new Promise(function (r, o) { var a = n.apply(t, e); function _next(n) { asyncGeneratorStep(a, r, o, _next, _throw, "next", n); } function _throw(n) { asyncGeneratorStep(a, r, o, _next, _throw, "throw", n); } _next(void 0); }); }; }
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
// ----------------------------------------------------------------------------
// Web Component: iamProductForm <iam-product-form>
// Extends: HTMLElement
// ----------------------------------------------------------------------------
// The central web component for managing the Product Form
// - It manages and coordinates the form initialization, product variant selection,
//   quantity changes, and form validation.
// - Sub-components are used to manage the various interactive form elements, but
//   this component centralizes the form state itself (e.g. the quantity selector
//   manages the qty input field UX, but this component is the source of truth for
//   the resulting quantity selected; similarly for variant selection, etc.)
// - It handles form submission over AJAX (i.e. when add-to-cart button triggers
//   the form submission, this component manages the submission).
// ----------------------------------------------------------------------------
// Customization:
// - Modify the `product-form.graphql` query to pull any custom product/variant
//   data that the form (or child elements) require, such as metafields
// - Import optional extensions to add additional functionality to the form
//   (e.g. sticky form, sale indicator, quantity limits, etc.)
// ----------------------------------------------------------------------------
// Event Listeners:
// - `formValidation:init`            → Initializes form validation when the validation
//                                      class is ready
// - `productForm:changed`            → Listens for when the form data changes
//                                      (option, selected variant, quantity, property)
//                                      to update the form validation state
// ----------------------------------------------------------------------------
// Event Triggers:
// - `productForm:init`               → Triggers when the product form is ready
//                                      for modules/extensions to hook in
// - `productForm:validation:init`    → Triggers when the form validation is initialized
//                                      (allowing modules to extend validation)
// - `productForm:validation:changed` → Triggers when the form validation state changes
// - `productForm:validation:success` → Triggers when the form validation is successful
// - `productForm:validation:failed`  → Triggers when the form validation fails
// - `productForm:changed`            → Triggers when the form data changes
//                                      (any of option, selected variant, quantity, property)
// - `productForm:option:changed`     → Triggers when the form data changes (option)
// - `productForm:variant:changed`    → Triggers when the form data changes (selected variant)
// - `productForm:quantity:changed`   → Triggers when the form data changes (quantity)
// - `productForm:property:changed`   → Triggers when the form data changes (property)
// - `productForm:submitting`         → Triggers when the form is being submitted
// - `productForm:submitted:success`  → Triggers when the form is successfully submitted
// - `productForm:submitted:error`    → Triggers when the form submission fails
// ----------------------------------------------------------------------------
// Event Filters:
// - `productForm:submitting:data`    → Allows filtering of the form data before submission
// ----------------------------------------------------------------------------
// Key Data Mutations:
// - Fetches product and custom data (product-form.graphql) and initializes the Shopify `ProductForm`
// - Toggles form loading and error states based on child components and validation states
// - Submits the form over AJAX after validation
// ----------------------------------------------------------------------------
// Gotchas:
// - Relies on promises to fetch product and custom data, which may fail if the API calls do not resolve successfully.
// - Custom product data is only fetched if a query is provided.
// - Error messages are injected into the `.form__errors-container` element
//   and the form is marked with a `--error` class until resolved
// ----------------------------------------------------------------------------

// -------------------------------------
// Dependencies
// -------------------------------------

// Helpers
import { ProductForm } from '@shopify/theme-product-form';
import { cart, product } from 'scripts/helpers/iamota-shopify-ajax';
import { storefrontApi } from 'scripts/helpers/iamota-shopify-storefront';
import iamForm from 'scripts/classes/iamForm';
import iamotaEventHandler from 'scripts/helpers/iamota-event-handler';
import registerCustomElement from 'scripts/helpers/register-custom-element';
import translate from 'scripts/helpers/iamota-translate';
import Debug from 'scripts/libs/debug';

// Custom Form Data Query
import productFormQuery from './product-form.graphql';

// Styles
import './_product-form.scss';

// Extensions
import './product-form--selling-plans';
import './product-form--updateUrl';
import './product-form--on-sale';
import './product-form--sticky';
import './product-variant-picker/product-variant-picker';
import './product-price/product-price';
import './quantity-input/quantity-input';
import './ordergroove-offer/ordergroove-offer';
import './add-to-cart-button/add-to-cart-button';
import 'snippets/product-gallery/product-gallery';
import './product-group-dropdown/product-group-dropdown';
import './product-form-v2';

// -------------------------------------
// Web Component
// -------------------------------------
class iamProductForm extends iamForm {
  constructor() {
    super();

    // Variables
    // Expose ProductForm methods
    _defineProperty(this, "getCurrentProduct", () => {
      var _this$ProductForm;
      var currentProduct = (_this$ProductForm = this.ProductForm) === null || _this$ProductForm === void 0 ? void 0 : _this$ProductForm.product;
      if (currentProduct && this.customData) {
        currentProduct.custom = this.customData.data.product;
      }
      return currentProduct;
    });
    _defineProperty(this, "getCurrentVariant", () => {
      var _this$ProductForm2;
      var currentVariant = (_this$ProductForm2 = this.ProductForm) === null || _this$ProductForm2 === void 0 ? void 0 : _this$ProductForm2.variant();
      if (currentVariant && this.customData) {
        var _this$customData$data;
        var customVariant = (_this$customData$data = this.customData.data) === null || _this$customData$data === void 0 || (_this$customData$data = _this$customData$data.product) === null || _this$customData$data === void 0 || (_this$customData$data = _this$customData$data.variants) === null || _this$customData$data === void 0 || (_this$customData$data = _this$customData$data.edges) === null || _this$customData$data === void 0 ? void 0 : _this$customData$data.find(variant => variant.node.id === "gid://shopify/ProductVariant/".concat(currentVariant.id));
        currentVariant.custom = customVariant === null || customVariant === void 0 ? void 0 : customVariant.node;
      }
      return currentVariant;
    });
    _defineProperty(this, "getCurrentOptions", () => {
      var _this$ProductForm3;
      return (_this$ProductForm3 = this.ProductForm) === null || _this$ProductForm3 === void 0 ? void 0 : _this$ProductForm3.options();
    });
    _defineProperty(this, "getCurrentProperties", () => {
      var _this$ProductForm4;
      return (_this$ProductForm4 = this.ProductForm) === null || _this$ProductForm4 === void 0 ? void 0 : _this$ProductForm4.properties();
    });
    _defineProperty(this, "getCurrentQuantity", () => {
      var _this$ProductForm5;
      return (_this$ProductForm5 = this.ProductForm) === null || _this$ProductForm5 === void 0 ? void 0 : _this$ProductForm5.quantity();
    });
    _defineProperty(this, "getCurrentSellingPlan", () => null);
    _defineProperty(this, "getCurrentPrice", () => {
      var sellingPlan = this.getCurrentSellingPlan();
      var variant = this.getCurrentVariant();
      return (sellingPlan === null || sellingPlan === void 0 ? void 0 : sellingPlan.price) || variant.price;
    });
    _defineProperty(this, "getCurrentData", () => {
      var currentData = {
        product: this.getCurrentProduct(),
        variant: this.getCurrentVariant(),
        options: this.getCurrentOptions(),
        properties: this.getCurrentProperties(),
        quantity: this.getCurrentQuantity(),
        sellingPlan: this.getCurrentSellingPlan(),
        price: this.getCurrentPrice()
      };
      //Debug.details('Product Form Data ↓', currentData);
      return currentData;
    });
    this.productData = null;
    this.customData = null;
    this.translations = {}; // Translation container (used by extensions)
  }
  connectedCallback() {
    var _this$dataset, _this$dataset2, _this$dataset3;
    this.setLoading(true);
    super.connectedCallback();
    this.sectionId = (_this$dataset = this.dataset) === null || _this$dataset === void 0 ? void 0 : _this$dataset.sectionId;
    this.productHandle = (_this$dataset2 = this.dataset) === null || _this$dataset2 === void 0 ? void 0 : _this$dataset2.productHandle;
    this.productId = (_this$dataset3 = this.dataset) === null || _this$dataset3 === void 0 ? void 0 : _this$dataset3.productId;
    this.variantIdInput = this.querySelector('input[name="id"]');
    if (!this.validateRequiredAttributes(['sectionId', 'productHandle', 'productId'])) return;

    // Initalize ProductForm
    var productFetchPromise = product.fetch(this.productHandle).then(productData => {
      this.productData = productData;
      this.ProductForm = new ProductForm(this.form, this.productData, {
        onOptionChange: this.onOptionChange.bind(this),
        onQuantityChange: this.onQuantityChange.bind(this),
        onPropertyChange: this.onPropertyChange.bind(this),
        onFormSubmit: this.onFormSubmit.bind(this)
      });
      var currentData = this.getCurrentData();
      this._setVariantInput(currentData.variant.id);
    });

    // Get Custom Product Data
    var productFormQueryPromise = productFormQuery ? storefrontApi.request(productFormQuery, {
      id: "gid://shopify/Product/".concat(this.productId)
    }).then(_ref => {
      var {
        data,
        extensions
      } = _ref;
      this.customData = {
        data,
        extensions
      };
    }) : Promise.resolve(); // If no query, resolve immediately as success.

    // Wait for both promises to resolve
    Promise.all([productFormQueryPromise, productFetchPromise]).then(() => {
      // Both promises resolved successfully
      Debug.details('Product Form Data ↓', this.getCurrentData());
      iamotaEventHandler.trigger('productForm:init', {
        isAtomic: true,
        iamForm: this
      }, {
        context: this,
        isAtomic: true
      });
    }).catch(error => {
      // Either of the promises failed
      console.error("Unable to initialize the product form: ".concat(error.message), error);
      this.setDisabled(true);
      this.setErrorState(translate('An error occurred, please try refreshing the page', 'productForm.error_product_data_fetch'));
    }).finally(() => {
      this.setLoading(false);
    });
  }
  onOptionChange(event) {
    this._setVariantInput(event.dataset.variant.id);
    iamotaEventHandler.trigger(['productForm:changed', 'productForm:option:changed', 'productForm:variant:changed'], _objectSpread(_objectSpread({
      iamProductForm: this
    }, event.dataset), {}, {
      event
    }), {
      context: this
    });
  }
  onQuantityChange(event) {
    iamotaEventHandler.trigger(['productForm:changed', 'productForm:quantity:changed'], _objectSpread(_objectSpread({
      iamProductForm: this
    }, event.dataset), {}, {
      event
    }), {
      context: this
    });
  }
  onPropertyChange(event) {
    iamotaEventHandler.trigger(['productForm:changed', 'productForm:property:changed'], _objectSpread(_objectSpread({
      iamProductForm: this
    }, event.dataset), {}, {
      event
    }), {
      context: this
    });
  }
  _setVariantInput(variantId) {
    this.variantIdInput.value = variantId;
  }

  /**
   * Handle form submission
   * @param {*} event
   * @returns
   */
  onFormSubmit(event) {
    var _this = this;
    return _asyncToGenerator(function* () {
      // Block the native form submission behavior
      event.preventDefault();

      // Block submitting the form in an invalid state
      if (!(yield _this.validateForm())) {
        _this.setErrorState(translate('Unable to submit the form with errors', 'productForm.error_validation'));
        return;
      }

      // Standardize Form data as an object
      // let formData = iamotaAjax.formToObject(this);
      var formData = new FormData(_this.form);
      formData = Object.fromEntries(formData.entries());

      // Allow form data to be modified before submission
      formData = yield iamotaEventHandler.applyFilters('productForm:submitting:data', formData, _objectSpread(_objectSpread({
        iamProductForm: _this
      }, event.dataset), {}, {
        event
      }));

      // Submit the form over AJAX
      iamotaEventHandler.trigger('productForm:submitting', {
        iamProductForm: _this,
        formData,
        event
      }, {
        context: _this
      });
      cart.add(formData).then(responseData => {
        iamotaEventHandler.trigger('productForm:submitted:success', {
          iamProductForm: _this,
          cart: responseData
        }, {
          context: _this
        });
      }).catch(error => {
        var _error$context;
        iamotaEventHandler.trigger('productForm:submitted:error', {
          iamProductForm: _this,
          error
        }, {
          context: _this
        });
        console.error(error);
        if ((_error$context = error.context) !== null && _error$context !== void 0 && (_error$context = _error$context.data) !== null && _error$context !== void 0 && _error$context.description) {
          _this.setErrorState(error.context.data.description);
        } else {
          _this.setErrorState(translate('Error adding item to cart', 'productForm.error_adding'));
        }
      });
    })();
  }
  onFormValidationInit(_ref2) {
    var {
      validator,
      form
    } = _ref2;
    super.onFormValidationInit({
      validator,
      form
    });
    // Prevent the form from submitting normally by just-validate.js so the
    // onFormSubmit can handle form submission over AJAX.
    this.form.addEventListener('submit', event => {
      event.preventDefault();
    });
  }
}
registerCustomElement('iam-product-form', iamProductForm);